/**
 * RoomGate — pre-join entry screen.
 *
 * Owner flow (PRD §4):
 *   1. Host opens https://hookum.app/ with no `?code` → "Create room"; the
 *      client mints a short room code, pushes it into the URL, and joins.
 *      Server creates the room lazily on first connect.
 *   2. Friend opens the shared link `https://hookum.app/?code=AB12CD` → the
 *      code is prefilled; they type a name and click "Join".
 *
 * The reclaim path is automatic: if `loadReclaimToken(code)` returns a value,
 * the socket transparently includes it on `join`; the server rebinds the seat
 * (ARCH §9.1). The user doesn't see "reconnect" UI — it just works.
 */
import { useEffect, useMemo, useState } from "react";
import { DISPLAY_NAME_REGEX } from "@hookum/protocol";
import { useConnection } from "../state/hooks.js";
import { loadName, loadReclaimToken } from "../state/storage.js";
import { actions } from "../wire/index.js";
import { HowToPlay, HowToPlayButton } from "./HowToPlay.js";

const CODE_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; // no I/O/0/1
const CODE_LEN = 6;

function mintCode(): string {
  const crypto = globalThis.crypto;
  const buf = new Uint32Array(CODE_LEN);
  crypto.getRandomValues(buf);
  let out = "";
  for (let i = 0; i < CODE_LEN; i++) {
    out += CODE_ALPHABET[buf[i]! % CODE_ALPHABET.length];
  }
  return out;
}

function codeFromUrl(): string {
  if (typeof window === "undefined") return "";
  const u = new URL(window.location.href);
  return (u.searchParams.get("code") ?? "").toUpperCase().slice(0, 12);
}

function pushCodeIntoUrl(code: string): void {
  if (typeof window === "undefined") return;
  const u = new URL(window.location.href);
  u.searchParams.set("code", code);
  window.history.replaceState({}, "", u.toString());
}

export function RoomGate(): JSX.Element {
  const conn = useConnection();
  const [code, setCode] = useState(() => codeFromUrl());
  const [name, setName] = useState(() => loadName());
  const [howToOpen, setHowToOpen] = useState(false);

  useEffect(() => {
    if (!code) return;
    pushCodeIntoUrl(code);
  }, [code]);

  const trimmedName = name.trim();
  const trimmedCode = code.trim().toUpperCase();
  // v1.4.1 (Phase 21.3): client mirrors the protocol regex so the host
  // sees inline feedback BEFORE pressing Join. The server still re-
  // validates at the wire layer — this is UX, not security. Only show
  // the error once the user has typed at least one character (avoids a
  // confusing red "invalid" hint on an empty field).
  const nameValid =
    trimmedName.length > 0 && DISPLAY_NAME_REGEX.test(trimmedName);
  const showNameError = trimmedName.length > 0 && !nameValid;
  const canJoin =
    nameValid &&
    trimmedCode.length >= 4 &&
    conn.kind !== "connecting" &&
    conn.kind !== "reconnecting";

  const hasReclaim = useMemo(
    () => (trimmedCode ? Boolean(loadReclaimToken(trimmedCode)) : false),
    [trimmedCode],
  );

  function onCreate(): void {
    const fresh = mintCode();
    setCode(fresh);
    pushCodeIntoUrl(fresh);
  }

  function onJoin(e: React.FormEvent): void {
    e.preventDefault();
    if (!canJoin) return;
    actions.joinRoom(trimmedCode, trimmedName);
  }

  return (
    <main
      className="grid min-h-screen place-items-center p-4"
      aria-labelledby="gate-title"
    >
      <div
        className="flex w-[min(400px,100%)] flex-col gap-[18px] rounded-lg border border-gold-400/35 bg-gradient-to-br from-ink-700 to-ink-900 px-6 py-7 shadow-[0_24px_60px_rgb(0_0_0/0.5)] max-[400px]:px-4 max-[400px]:py-5"
      >
        {/* Phase 7: `gate-title` className kept only because the shared
            `.lobby-title, .gate-title { text-shadow }` rule still references
            it; Lobby will migrate next and the rule will be split then. */}
        <h1
          id="gate-title"
          className="gate-title m-0 text-[32px] font-bold tracking-[1px] text-gold-400 max-[400px]:text-[26px]"
        >
          Hookum
        </h1>
        <p className="m-0 text-sm opacity-80">
          {hasReclaim
            ? "Welcome back — your seat is waiting."
            : "Create a room, share the link, deal."}
        </p>
        <form
          onSubmit={onJoin}
          className="flex flex-col gap-[14px]"
          aria-label="Join a room"
        >
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="text-xs uppercase tracking-[0.08em] opacity-80">
              Display name
            </span>
            <input
              type="text"
              value={name}
              maxLength={32}
              autoComplete="nickname"
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Amaar"
              className="w-full"
              aria-invalid={showNameError || undefined}
              aria-describedby={showNameError ? "name-error" : undefined}
            />
            {showNameError && (
              <span
                id="name-error"
                role="alert"
                className="text-xs text-red-300"
              >
                Use letters, numbers, punctuation, and spaces only.
              </span>
            )}
          </label>
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="text-xs uppercase tracking-[0.08em] opacity-80">
              Room code
            </span>
            <input
              type="text"
              value={code}
              maxLength={12}
              autoCapitalize="characters"
              spellCheck={false}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="ABCDE"
              className="w-full"
            />
          </label>
          <div className="flex flex-wrap gap-2.5">
            <button
              type="button"
              onClick={onCreate}
              className="min-h-11 flex-1 font-semibold"
            >
              Create room
            </button>
            <button
              type="submit"
              disabled={!canJoin}
              className="min-h-11 flex-1 border-gold-400 bg-gold-400 font-bold text-ink-900 enabled:hover:border-[#f0c97a] enabled:hover:bg-[#f0c97a]"
            >
              {hasReclaim ? "Rejoin" : "Join"}
            </button>
          </div>
        </form>
        <div className="mt-[18px] flex items-center justify-center gap-2 text-sm text-white/60">
          <HowToPlayButton onOpen={() => setHowToOpen(true)} />
          <span className="tracking-[0.02em]">How to play</span>
        </div>
      </div>
      <HowToPlay open={howToOpen} onClose={() => setHowToOpen(false)} />
    </main>
  );
}
